import { format as formatNumber } from 'd3-format'
import type { ChartIcicleChartConfig, ChartIcicleNode, ChartIcicleTooltipEntry } from './types'

export const DEFAULT_ICICLE_FILL = '#94a3b8'
export const DEFAULT_TILE_BREADTH = 160
export const DEFAULT_GAP = 4
export const DEFAULT_MIN_LABEL_SIZE = 32

export function getIcicleNodeSize(node: ChartIcicleNode): number {
  return typeof node.value === 'number' && Number.isFinite(node.value) ? Math.max(0, node.value) : 0
}

export function nodeHasChildren(node: ChartIcicleNode): boolean {
  return Array.isArray(node.children) && node.children.length > 0
}

export function getVisibleChildren(node: ChartIcicleNode, collapsedIds: ReadonlySet<string>): ChartIcicleNode[] {
  if (collapsedIds.has(node.id) || !node.children) {
    return []
  }
  return node.children
}

export function getVisibleIcicleForest(nodes: ChartIcicleNode[], collapsedIds: ReadonlySet<string>): ChartIcicleNode[] {
  return nodes.map((node) => getVisibleIcicleNode(node, collapsedIds))
}

function getVisibleIcicleNode(node: ChartIcicleNode, collapsedIds: ReadonlySet<string>): ChartIcicleNode {
  if (collapsedIds.has(node.id) || !nodeHasChildren(node)) {
    return { ...node, children: [] }
  }
  return {
    ...node,
    children: node.children?.map((child) => getVisibleIcicleNode(child, collapsedIds)),
  }
}

export function resolveIcicleFill(node: ChartIcicleNode, config: ChartIcicleChartConfig['config']): string {
  if (typeof node.color === 'string' && node.color.length > 0) {
    return node.color
  }
  if (node.colorKey) {
    const mapped = config[node.colorKey]?.color
    if (mapped) {
      return mapped
    }
  }
  return DEFAULT_ICICLE_FILL
}

export function resolveIcicleColorLabel(
  node: ChartIcicleNode,
  config: ChartIcicleChartConfig['config']
): string | undefined {
  if (!node.colorKey) {
    return undefined
  }
  return config[node.colorKey]?.label ?? node.colorKey
}

export function getIcicleDisplayValue(node: ChartIcicleNode): string | number {
  return node.displayValue ?? node.value
}

export function formatIcicleValue(value: string | number, valueFormat?: string): string {
  if (typeof value === 'string') {
    return value
  }
  if (!Number.isFinite(value)) {
    return ''
  }
  if (valueFormat) {
    try {
      return formatNumber(valueFormat)(value)
    } catch {
      return String(value)
    }
  }
  return String(value)
}

export function buildIcicleTooltipRows(
  node: ChartIcicleNode,
  config: ChartIcicleChartConfig['config'],
  formattedValue: string
): ChartIcicleTooltipEntry[] {
  const rows: ChartIcicleTooltipEntry[] = [
    { label: 'Name', value: node.name },
    { label: 'Value', value: formattedValue },
  ]
  const colorLabel = resolveIcicleColorLabel(node, config)
  if (colorLabel) {
    rows.push({ label: 'Colour', value: colorLabel })
  }
  if (node.tooltip?.length) {
    rows.push(...node.tooltip)
  }
  return rows
}
